import { vercel } from "@t3-oss/env-core/presets-zod";
import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  extends: [vercel()],
  server: {
    NODE_ENV: z.string().min(1),
    FLAGS_SECRET: z.string().min(1),
    RESEND_SECRET: z.string().min(1),
    SMTP_HOST: z.string().min(1),
    SMTP_PORT: z.string().min(1),
    SMTP_FROM: z.string().min(1),
    POSTHOG_KEY_PROJECT_ID: z.string().min(1).optional(),
    POSTHOG_PERSONAL_API_KEY: z.string().min(1).optional(),
    POSTHOG_REGION: z.enum(["us", "eu"]).optional(),
    POSTHOG_LOG_EXPORT_ENABLED: z.string().optional(),
    POSTHOG_LOG_EXPORT_HOST: z.string().optional(),
    GROQ_API_KEY: z.string().min(1),
    SERVER_SECRET: z.string().min(1),
    AUTH_URL: z.string().optional(),
    DATABASE_URL: z.string().min(1),
    SUPABASE_SECRET_KEY: z.string().startsWith("sb_secret_"),
    SUPABASE_REALTIME_SIGNING_KEY: z.string().min(1),
    AUTH_SECRET:
      process.env.NODE_ENV === "production"
        ? z.string().min(1)
        : z.string().min(1).optional(),
    AUTH_GOOGLE_ID: z.string().min(1),
    AUTH_GOOGLE_SECRET: z.string().min(1),
    SLACK_APP_ID: z.string().min(1),
    SLACK_CLIENT_ID: z.string().min(1),
    SLACK_CLIENT_SECRET: z.string().min(1),
    SLACK_SIGNING_SECRET: z.string().min(1),
    SLACK_VERIFICATION_TOKEN: z.string().min(1),
    SLACK_WEBHOOK_URL_SUPPORT_CHANNEL: z.string().min(1),
    SLACK_WEBHOOK_URL_FEEDBACK_CHANNEL: z.string().min(1),
    SLACK_WEBHOOK_URL_ONBOARDING_REQUESTS_CHANNEL: z.string().min(1),
    INNGEST_SIGNING_KEY: z.string().optional(),
    VETTING_ENCRYPTION_KEY: z
      .string()
      .regex(
        /^[0-9a-fA-F]{64}$/,
        "VETTING_ENCRYPTION_KEY must be 64 hex chars (32 bytes). Generate with `openssl rand -hex 32`."
      ),
    OMNICHECK_BASE_URL:
      process.env.VERCEL_ENV === "production"
        ? z.string().min(1)
        : z.string().min(1).optional(),
    OMNICHECK_API_KEY:
      process.env.VERCEL_ENV === "production"
        ? z.string().min(1)
        : z.string().min(1).optional(),
    TWILIO_ACCOUNT_SID:
      process.env.VERCEL_ENV === "production"
        ? z.string().regex(/^AC[0-9a-fA-F]{32}$/)
        : z
            .string()
            .regex(/^AC[0-9a-fA-F]{32}$/)
            .optional(),
    TWILIO_AUTH_TOKEN:
      process.env.VERCEL_ENV === "production"
        ? z.string().min(1)
        : z.string().min(1).optional(),
    TWILIO_VERIFY_SERVICE_SID:
      process.env.VERCEL_ENV === "production"
        ? z.string().regex(/^VA[0-9a-fA-F]{32}$/)
        : z
            .string()
            .regex(/^VA[0-9a-fA-F]{32}$/)
            .optional(),
    OPENROUTER_API_KEY: z.string().min(1),
    MAPBOX_ACCESS_TOKEN: z.string().min(1),
    USAGE_COST_OMNICHECK_ID_VERIFICATION_CENTS: z.coerce
      .number()
      .int()
      .nonnegative()
      .optional(),
    USAGE_COST_TWILIO_PHONE_OTP_CENTS: z.coerce
      .number()
      .int()
      .nonnegative()
      .optional(),
    QA_LOGIN_SECRET: z.string().min(32).optional(),
    QA_LOGIN_EMAILS: z.string().optional(),
    GOOGLE_INDEXING_ENABLED: z.enum(["true", "false"]).default("false"),
    GOOGLE_INDEXING_SITE_ORIGIN: z
      .string()
      .url()
      .default("https://www.vacwork.app"),
    GOOGLE_SERVICE_ACCOUNT_EMAIL: z.string().email().optional(),
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY: z.string().optional(),
    GOOGLE_WORKSPACE_IMPERSONATE_USER: z.string().email().optional(),
    PILOT_SELF_SERVE_LOCK_DEFAULT: z.enum(["true", "false"]).optional(),
    FIXED_JOB_TEMPLATES_DEFAULT: z.enum(["true", "false"]).optional(),
  },
  client: {
    // react-doctor-disable-next-line react-doctor/public-env-secret-name -- Mapbox public access token (pk.*) is a publishable, browser-safe value
    NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN: z.string().min(1),
    NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_LIGHT: z.string().min(1),
    NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_DARK: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().min(1),
    NEXT_PUBLIC_SUPABASE_URL: z.url(),
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: z
      .string()
      .refine(
        (value) =>
          value.startsWith("sb_publishable_") || value.startsWith("eyJ"),
        "Expected a Supabase publishable key or legacy anon JWT"
      ),
    NEXT_PUBLIC_AUTH_GOOGLE_ID: z.string().min(1),
    NEXT_PUBLIC_GA_MEASUREMENT_ID: z.string().optional(),
    NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_VERCEL_TARGET_ENV: z.string().optional(),
  },
  runtimeEnv: {
    FLAGS_SECRET: process.env.FLAGS_SECRET,
    NODE_ENV: process.env.NODE_ENV ?? "development",
    RESEND_SECRET: process.env.RESEND_SECRET,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_FROM: process.env.SMTP_FROM,
    POSTHOG_KEY_PROJECT_ID: process.env.POSTHOG_KEY_PROJECT_ID,
    POSTHOG_PERSONAL_API_KEY: process.env.POSTHOG_PERSONAL_API_KEY,
    POSTHOG_REGION: process.env.POSTHOG_REGION,
    POSTHOG_LOG_EXPORT_ENABLED: process.env.POSTHOG_LOG_EXPORT_ENABLED,
    POSTHOG_LOG_EXPORT_HOST: process.env.POSTHOG_LOG_EXPORT_HOST,
    SERVER_SECRET: process.env.SERVER_SECRET,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
    AUTH_URL: process.env.AUTH_URL,
    DATABASE_URL: process.env.DATABASE_URL,
    SUPABASE_SECRET_KEY: process.env.SUPABASE_SECRET_KEY,
    SUPABASE_REALTIME_SIGNING_KEY: process.env.SUPABASE_REALTIME_SIGNING_KEY,
    AUTH_SECRET: process.env.AUTH_SECRET,
    AUTH_GOOGLE_ID: process.env.AUTH_GOOGLE_ID,
    AUTH_GOOGLE_SECRET: process.env.AUTH_GOOGLE_SECRET,
    SLACK_APP_ID: process.env.SLACK_APP_ID,
    SLACK_CLIENT_ID: process.env.SLACK_CLIENT_ID,
    SLACK_CLIENT_SECRET: process.env.SLACK_CLIENT_SECRET,
    SLACK_SIGNING_SECRET: process.env.SLACK_SIGNING_SECRET,
    SLACK_VERIFICATION_TOKEN: process.env.SLACK_VERIFICATION_TOKEN,
    SLACK_WEBHOOK_URL_SUPPORT_CHANNEL:
      process.env.SLACK_WEBHOOK_URL_SUPPORT_CHANNEL,
    SLACK_WEBHOOK_URL_FEEDBACK_CHANNEL:
      process.env.SLACK_WEBHOOK_URL_FEEDBACK_CHANNEL,
    SLACK_WEBHOOK_URL_ONBOARDING_REQUESTS_CHANNEL:
      process.env.SLACK_WEBHOOK_URL_ONBOARDING_REQUESTS_CHANNEL,
    INNGEST_SIGNING_KEY: process.env.INNGEST_SIGNING_KEY,
    VETTING_ENCRYPTION_KEY: process.env.VETTING_ENCRYPTION_KEY,
    OMNICHECK_BASE_URL: process.env.OMNICHECK_BASE_URL,
    OMNICHECK_API_KEY: process.env.OMNICHECK_API_KEY,
    TWILIO_ACCOUNT_SID: process.env.TWILIO_ACCOUNT_SID,
    TWILIO_AUTH_TOKEN: process.env.TWILIO_AUTH_TOKEN,
    TWILIO_VERIFY_SERVICE_SID: process.env.TWILIO_VERIFY_SERVICE_SID,
    NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN:
      process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
    NEXT_PUBLIC_MAPBOX_STYLE_FRANK: process.env.NEXT_PUBLIC_MAPBOX_STYLE_FRANK,
    NEXT_PUBLIC_MAPBOX_STYLE_STREETS:
      process.env.NEXT_PUBLIC_MAPBOX_STYLE_STREETS,
    NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_LIGHT:
      process.env.NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_LIGHT,
    NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_DARK:
      process.env.NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_DARK,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_AUTH_GOOGLE_ID: process.env.NEXT_PUBLIC_AUTH_GOOGLE_ID,
    NEXT_PUBLIC_GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
    NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
    NEXT_PUBLIC_VERCEL_TARGET_ENV: process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV,
    OPENROUTER_API_KEY: process.env.OPENROUTER_API_KEY,
    MAPBOX_ACCESS_TOKEN: process.env.MAPBOX_ACCESS_TOKEN,
    USAGE_COST_OMNICHECK_ID_VERIFICATION_CENTS:
      process.env.USAGE_COST_OMNICHECK_ID_VERIFICATION_CENTS,
    USAGE_COST_TWILIO_PHONE_OTP_CENTS:
      process.env.USAGE_COST_TWILIO_PHONE_OTP_CENTS,
    QA_LOGIN_SECRET: process.env.QA_LOGIN_SECRET,
    QA_LOGIN_EMAILS: process.env.QA_LOGIN_EMAILS,
    GOOGLE_INDEXING_ENABLED: process.env.GOOGLE_INDEXING_ENABLED ?? "false",
    GOOGLE_INDEXING_SITE_ORIGIN:
      process.env.GOOGLE_INDEXING_SITE_ORIGIN ?? "https://www.vacwork.app",
    GOOGLE_SERVICE_ACCOUNT_EMAIL: process.env.GOOGLE_SERVICE_ACCOUNT_EMAIL,
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY:
      process.env.GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY,
    GOOGLE_WORKSPACE_IMPERSONATE_USER:
      process.env.GOOGLE_WORKSPACE_IMPERSONATE_USER,
    PILOT_SELF_SERVE_LOCK_DEFAULT: process.env.PILOT_SELF_SERVE_LOCK_DEFAULT,
    FIXED_JOB_TEMPLATES_DEFAULT: process.env.FIXED_JOB_TEMPLATES_DEFAULT,
  },
});
